import { useCallback } from "react";
import {
  Activity,
  AtSign,
  Building2,
  CalendarClock,
  Clock,
  Computer,
  ExternalLink,
  Globe,
  Package,
  PcCase,
  Router,
  User,
  Wifi,
  Zap,
} from "lucide-react";

import { Badge } from "components/ui/badge";
import { buttonVariants } from "components/ui/button";
import { Separator } from "components/ui/separator";
import { MachineApprovalSelect } from "components/CaseMap/MachineApprovalSelect";
import { ToggleEventsButton } from "components/CaseMap/ToggleEventsButton";
import { arrToStrList, objToStrList } from "components/CaseMap/helpers";
import { DetailItem } from "components/DetailItem";
import { formatDate } from "components/helpers";
import useCaseMapStore from "stores/useCaseMapStore";

export function MachineInfoCard({ machine, canEditStatus, position }) {
  const eventOverlayIds = useCaseMapStore(s => s.eventOverlayIds);
  const showEventsFor = useCaseMapStore(s => s.showEventsFor);
  const dismissEvents = useCaseMapStore(s => s.dismissEvents);
  const closeSummary = useCaseMapStore(s => s.closeSummary);

  const showingThisMachine =
    eventOverlayIds?.length === 1 && eventOverlayIds[0] === machine.id;

  // The overlay lands on and around the pin this summary is anchored to, so the
  // summary steps aside once it has been asked for the events. The machine stays
  // selected, and reopening the pin offers to hide them again.
  const handleShowEvents = useCallback(() => {
    if (showingThisMachine) {
      dismissEvents();
      return;
    }
    showEventsFor([machine.id]);
    closeSummary();
  }, [showingThisMachine, dismissEvents, showEventsFor, closeSummary, machine]);

  return (
    <div className="tw:flex tw:max-h-80 tw:min-w-64 tw:flex-col tw:gap-2 tw:p-2">
      <div className="tw:flex tw:items-center tw:gap-2">
        <span className="tw:font-semibold tw:text-sm tw:text-(--color-text-primary)">
          {machine.machine_id}
        </span>
        <Badge variant="outline">{`auto: ${machine.automation_status}`}</Badge>
      </div>
      <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-1.5 tw:overflow-y-auto">
        <DetailItem
          icon={Zap}
          label="Events"
          content={`${machine.num_events ?? 0}`}
        />
        <DetailItem
          icon={Clock}
          label="Last event"
          content={machine.last_event ? formatDate(machine.last_event) : null}
        />
        <DetailItem
          icon={CalendarClock}
          label="First event"
          content={machine.first_event ? formatDate(machine.first_event) : null}
        />
        <DetailItem
          icon={Package}
          label="Products"
          content={arrToStrList(machine?.products)}
        />
        <DetailItem
          icon={Activity}
          label="Event types"
          content={objToStrList(machine.event_types)}
        />
        <DetailItem
          icon={PcCase}
          label="Hostname"
          content={arrToStrList(machine.hostname)}
        />
        <DetailItem
          icon={User}
          label="Username"
          content={arrToStrList(machine.username)}
        />
        <DetailItem
          icon={Wifi}
          label="WiFi AP"
          content={arrToStrList(machine.active_wifi_access_point)}
        />
        <DetailItem
          icon={Globe}
          label="Public IP"
          content={objToStrList(machine.public_ip_address)}
        />

        <DetailItem
          icon={Building2}
          label="IP organization"
          content={objToStrList(machine.public_ip_organization)}
        />
        <DetailItem
          icon={AtSign}
          label="Email domains"
          content={objToStrList(machine.email_domains)}
        />
        <DetailItem
          icon={Computer}
          label="Computer domains"
          content={objToStrList(machine?.computer_domains)}
        />
        <DetailItem
          icon={Router}
          label="Active MAC"
          content={machine.active_mac}
        />
      </div>
      <Separator />

      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        {canEditStatus ? (
          <MachineApprovalSelect
            value={machine.machine_approval_status_id}
            machineIds={[machine.id]}
          />
        ) : (
          <Badge variant="secondary" className="tw:w-fit">
            {machine.machine_approval_status || "Pending"}
          </Badge>
        )}
        <div className="tw:flex  tw:items-center tw:gap-2">
          <ToggleEventsButton
            isShowing={showingThisMachine}
            onToggle={handleShowEvents}
          />
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${position.lat},${position.lng}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open in Google Maps"
            className={buttonVariants({ variant: "outline", size: "icon" })}
          >
            <ExternalLink />
          </a>
        </div>
      </div>
    </div>
  );
}
